import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Loader2, Save, X, Percent, DollarSign, Gift } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/ui/use-toast';
import UnifiedPassPreview from '@/components/loyalty/UnifiedPassPreview';
import {
  validateImageDimensions,
  validatePNGFormat,
  generateMultipleResolutions,
  type ImageType,
  type ResizedImages
} from '@/lib/imageUtils';
import { isBackgroundDark } from '@/lib/core/utils';
import { deleteReplacedCouponImages } from '@/lib/couponImageCleanup';
import { PRIMARY_COLOR } from '@/lib/constants/colors';

interface CouponOfferBuilderForm {
  // Basic Info
  header: string;
  name: string;

  // Discount Settings
  discountType: 'percentage' | 'fixed_amount' | 'free_item';
  discountValue: number;

  // Validity Period
  validFrom: string;
  validUntil: string;

  // Visual Design
  backgroundColor: string;

  // Apple Wallet Logo (rectangular OR square)
  appleLogoUrl: string;
  appleLogoFile?: File;
  appleLogoBase64?: string;
  appleLogoType?: 'rectangular' | 'square';

  // Google Wallet Logo (square only)
  googleLogoUrl: string;
  googleLogoFile?: File;
  googleLogoBase64?: string;

  // Hero/Strip Image
  heroImageUrl: string;
  heroImageFile?: File;
  heroImageBase64?: string;
}

interface CouponOfferBuilderProps {
  businessId: string;
  offer?: any;
  onComplete: (couponOffer: any) => void;
  onCancel: () => void;
}

const CouponOfferBuilder: React.FC<CouponOfferBuilderProps> = ({
  businessId,
  offer,
  onComplete,
  onCancel
}) => {
  const { user } = useAuth();
  const [businessName, setBusinessName] = useState<string>('');

  // localStorage key for draft data
  const DRAFT_KEY = offer
    ? `couponOfferEditDraft_${businessId}_${offer.id}`
    : `couponOfferDraft_${businessId}`;

  // Load draft from localStorage or use defaults
  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error loading coupon draft:', error);
      return null;
    }
  };

  const draft = loadDraft();

  // Helper to convert base64 to File
  const base64ToFile = (base64: string, filename: string): File => {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const getInitialFormData = (): CouponOfferBuilderForm => {
    // If loading from draft, convert base64 back to File objects
    if (draft?.formData) {
      const draftData = draft.formData;
      return {
        ...draftData,
        appleLogoFile: draftData.appleLogoBase64 ? base64ToFile(draftData.appleLogoBase64, 'apple-logo.png') : undefined,
        googleLogoFile: draftData.googleLogoBase64 ? base64ToFile(draftData.googleLogoBase64, 'google-logo.png') : undefined,
        heroImageFile: draftData.heroImageBase64 ? base64ToFile(draftData.heroImageBase64, 'hero-image.png') : undefined
      };
    }

    if (offer) {
      return {
        header: offer.header || '',
        name: offer.name || '',
        discountType: offer.discount_type || 'percentage',
        discountValue: offer.discount_value || 10,
        validFrom: offer.valid_from ? new Date(offer.valid_from).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        validUntil: offer.valid_until ? new Date(offer.valid_until).toISOString().split('T')[0] : '',
        backgroundColor: offer.background_color || '#d97c12',
        appleLogoUrl: offer.apple_logo_urls?.['3x'] || '',
        appleLogoFile: undefined,
        appleLogoBase64: undefined,
        appleLogoType: offer.apple_logo_urls ? 'square' : undefined,
        googleLogoUrl: offer.google_logo_url || '',
        googleLogoFile: undefined,
        googleLogoBase64: undefined,
        heroImageUrl: offer.hero_image_urls?.['3x'] || '',
        heroImageFile: undefined,
        heroImageBase64: undefined
      };
    }

    // Default values
    return {
      header: '',
      name: '',
      discountType: 'percentage',
      discountValue: 10,
      validFrom: new Date().toISOString().split('T')[0],
      validUntil: '',
      backgroundColor: '#d97c12',
      appleLogoUrl: '',
      appleLogoFile: undefined,
      appleLogoBase64: undefined,
      appleLogoType: undefined,
      googleLogoUrl: '',
      googleLogoFile: undefined,
      googleLogoBase64: undefined,
      heroImageUrl: '',
      heroImageFile: undefined,
      heroImageBase64: undefined
    };
  };

  const [previewType, setPreviewType] = useState<'google' | 'apple'>(draft?.previewType || 'google');
  const [formData, setFormData] = useState<CouponOfferBuilderForm>(getInitialFormData());
  const [isCreating, setIsCreating] = useState(false);
  const [savedCouponId, setSavedCouponId] = useState<string | null>(offer?.id || null);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [dragTarget, setDragTarget] = useState<'appleLogo' | 'googleLogo' | 'heroImage' | null>(null);
  const dragCounterRef = React.useRef(0);
  const dragTargetTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Debounce color updates to prevent lag
  const colorUpdateTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Save draft to localStorage whenever form data changes
  useEffect(() => {
    const draftData = {
      formData,
      previewType,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));
    } catch (error) {
      console.error('Error saving coupon draft:', error);
    }
  }, [formData, previewType, DRAFT_KEY]);

  // Clear draft on successful save or cancel
  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (error) {
      console.error('Error clearing coupon draft:', error);
    }
  };

  useEffect(() => {
    if (businessId) {
      fetchBusinessData();
    }
  }, [businessId]);

  // Update form data when offer becomes available in edit mode
  useEffect(() => {
    if (offer && !draft) {
      setFormData({
        header: offer.header || '',
        name: offer.name || '',
        discountType: offer.discount_type || 'percentage',
        discountValue: offer.discount_value || 10,
        validFrom: offer.valid_from ? new Date(offer.valid_from).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        validUntil: offer.valid_until ? new Date(offer.valid_until).toISOString().split('T')[0] : '',
        backgroundColor: offer.background_color || '#d97c12',
        appleLogoUrl: offer.apple_logo_urls?.['3x'] || '',
        appleLogoFile: undefined,
        appleLogoBase64: undefined,
        appleLogoType: offer.apple_logo_urls ? 'square' : undefined,
        googleLogoUrl: offer.google_logo_url || '',
        googleLogoFile: undefined,
        googleLogoBase64: undefined,
        heroImageUrl: offer.hero_image_urls?.['3x'] || '',
        heroImageFile: undefined,
        heroImageBase64: undefined
      });
    }
  }, [offer]);

  const fetchBusinessData = async () => {
    if (!businessId) return;

    try {
      const { data: business, error } = await supabase
        .from('businesses')
        .select('name')
        .eq('id', businessId)
        .single();

      if (error) throw error;
      if (business?.name) {
        setBusinessName(business.name);
        // Pre-fill header with business name for new coupons (only if no draft and no offer)
        if (!offer && !draft) {
          setFormData(prev => ({ ...prev, header: business.name }));
        }
      }
    } catch (error) {
      console.error('Error fetching business data:', error);
    }
  };

  // Global drag handlers
  useEffect(() => {
    const handleGlobalDragEnter = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current++;
      if (dragCounterRef.current === 1) {
        setIsDragging(true);
      }
    };

    const handleGlobalDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
        setDragTarget(null);
      }
    };

    const handleGlobalDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleGlobalDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);
      setDragTarget(null);
    };

    document.addEventListener('dragenter', handleGlobalDragEnter);
    document.addEventListener('dragleave', handleGlobalDragLeave);
    document.addEventListener('dragover', handleGlobalDragOver);
    document.addEventListener('drop', handleGlobalDrop);

    return () => {
      document.removeEventListener('dragenter', handleGlobalDragEnter);
      document.removeEventListener('dragleave', handleGlobalDragLeave);
      document.removeEventListener('dragover', handleGlobalDragOver);
      document.removeEventListener('drop', handleGlobalDrop);
    };
  }, []);

  const processImageFile = async (
    file: File,
    imageType: 'appleLogo' | 'googleLogo' | 'heroImage'
  ) => {
    // Validate file type - must be image
    if (!file.type.startsWith('image/')) {
      toast.error('Invalid file type', {
        description: 'Please upload an image file'
      });
      return false;
    }

    // Validate PNG format requirement
    const formatValidation = validatePNGFormat(file);
    if (!formatValidation.valid) {
      toast.error('Invalid file format', {
        description: formatValidation.message
      });
      return false;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large', {
        description: 'Image must be smaller than 5MB'
      });
      return false;
    }

    // For Apple logo, try both rectangular and square formats
    if (imageType === 'appleLogo') {
      const rectValidation = await validateImageDimensions(file, 'logo');

      if (rectValidation.valid) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target?.result as string;
          setFormData(prev => ({
            ...prev,
            appleLogoFile: file,
            appleLogoBase64: base64,
            appleLogoType: 'rectangular',
            appleLogoUrl: ''
          }));
        };
        reader.readAsDataURL(file);

        toast.success('Apple logo ready', {
          description: 'Rectangular format (480×150) validated'
        });
        return true;
      }

      const squareValidation = await validateImageDimensions(file, 'logo_square');

      if (squareValidation.valid) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target?.result as string;
          setFormData(prev => ({
            ...prev,
            appleLogoFile: file,
            appleLogoBase64: base64,
            appleLogoType: 'square',
            appleLogoUrl: ''
          }));
        };
        reader.readAsDataURL(file);

        toast.success('Apple logo ready', {
          description: 'Square format (150×150) validated'
        });
        return true;
      }

      toast.error('Invalid Apple logo dimensions', {
        description: `Image must be either 480 × 150 pixels (rectangular) or 150 × 150 pixels (square). Your image is ${rectValidation.actualWidth} × ${rectValidation.actualHeight} pixels.`
      });
      return false;
    }

    // For Google logo, only accept square (660×660)
    if (imageType === 'googleLogo') {
      const validation = await validateImageDimensions(file, 'logo_google_square');

      if (!validation.valid) {
        toast.error('Invalid Google logo dimensions', {
          description: validation.message
        });
        return false;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        setFormData(prev => ({
          ...prev,
          googleLogoFile: file,
          googleLogoBase64: base64,
          googleLogoUrl: ''
        }));
      };
      reader.readAsDataURL(file);

      toast.success('Google logo ready', {
        description: 'Square format (660×660) validated'
      });
      return true;
    }

    // For hero/strip image
    if (imageType === 'heroImage') {
      const validation = await validateImageDimensions(file, 'strip');

      if (!validation.valid) {
        toast.error('Incorrect image dimensions', {
          description: validation.message
        });
        return false;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        setFormData(prev => ({
          ...prev,
          heroImageFile: file,
          heroImageBase64: base64,
          heroImageUrl: ''
        }));
      };
      reader.readAsDataURL(file);

      toast.success('Hero image ready', {
        description: 'Strip format (1125×432) validated'
      });
      return true;
    }
    return false;
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    imageType: 'appleLogo' | 'googleLogo' | 'heroImage'
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset the input so the same file can be selected again
    event.target.value = '';

    await processImageFile(file, imageType);
  };

  const removeImage = (imageType: 'appleLogo' | 'googleLogo' | 'heroImage') => {
    if (imageType === 'appleLogo') {
      setFormData(prev => ({
        ...prev,
        appleLogoFile: undefined,
        appleLogoBase64: undefined,
        appleLogoType: undefined,
        appleLogoUrl: ''
      }));
    } else if (imageType === 'googleLogo') {
      setFormData(prev => ({
        ...prev,
        googleLogoFile: undefined,
        googleLogoBase64: undefined,
        googleLogoUrl: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        heroImageFile: undefined,
        heroImageBase64: undefined,
        heroImageUrl: ''
      }));
    }
  };

  /**
   * Upload a single image file to Supabase storage
   */
  const uploadSingleImage = async (
    file: File,
    imageType: 'appleLogo' | 'googleLogo' | 'heroImage',
    resolution: '1x' | '2x' | '3x' = '3x'
  ): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const timestamp = Date.now();
    const fileName = `${businessId}_coupon_${imageType}_${resolution}_${timestamp}.${fileExt}`;

    console.log(`Uploading ${imageType} (${resolution}):`, fileName);

    try {
      const { error: uploadError } = await supabase.storage
        .from('business-assets')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      const { data: urlData } = supabase.storage
        .from('business-assets')
        .getPublicUrl(fileName);

      if (!urlData?.publicUrl) {
        throw new Error('Failed to get public URL');
      }

      console.log(`${imageType} (${resolution}) uploaded successfully:`, urlData.publicUrl);
      return urlData.publicUrl;
    } catch (error) {
      console.error(`${imageType} (${resolution}) upload error:`, error);
      throw new Error(`${imageType} (${resolution}) upload failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Upload multiple resolution images for Apple Wallet
   */
  const uploadMultipleResolutions = async (
    originalFile: File,
    imageType: 'appleLogo' | 'heroImage',
    appleLogoType?: 'rectangular' | 'square'
  ): Promise<{ '1x': string; '2x': string; '3x': string }> => {
    try {
      let resolutionType: ImageType;
      if (imageType === 'appleLogo') {
        resolutionType = appleLogoType === 'rectangular' ? 'logo' : 'logo_square';
      } else {
        resolutionType = 'strip';
      }

      const resizedImages = await generateMultipleResolutions(originalFile, resolutionType);

      const [url1x, url2x, url3x] = await Promise.all([
        uploadSingleImage(resizedImages['1x'], imageType, '1x'),
        uploadSingleImage(resizedImages['2x'], imageType, '2x'),
        uploadSingleImage(resizedImages['3x'], imageType, '3x')
      ]);

      return {
        '1x': url1x,
        '2x': url2x,
        '3x': url3x
      };
    } catch (error) {
      throw new Error(`Failed to upload multiple resolutions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const getDiscountTypeConfig = (type: 'percentage' | 'fixed_amount' | 'free_item') => {
    switch (type) {
      case 'percentage':
        return {
          label: 'Percentage Off',
          icon: <Percent className="h-4 w-4" />,
          description: 'Percentage discount off total purchase'
        };
      case 'fixed_amount':
        return {
          label: 'Fixed Amount Off',
          icon: <DollarSign className="h-4 w-4" />,
          description: 'Fixed dollar amount off total purchase'
        };
      case 'free_item':
        return {
          label: 'Free Item',
          icon: <Gift className="h-4 w-4" />,
          description: 'Free item or service'
        };
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter an offer name');
      return false;
    }

    if (!formData.header.trim()) {
      toast.error('Please enter a header for your coupon');
      return false;
    }

    if (formData.discountType !== 'free_item' && (!formData.discountValue || formData.discountValue <= 0)) {
      toast.error('Please enter a valid discount value');
      return false;
    }

    if (formData.discountType === 'percentage' && formData.discountValue > 100) {
      toast.error('Percentage discount cannot exceed 100%');
      return false;
    }

    if (formData.validUntil && new Date(formData.validUntil) <= new Date(formData.validFrom)) {
      toast.error('End date must be after start date');
      return false;
    }

    // Validate all required images
    if (!formData.appleLogoUrl && !formData.appleLogoFile) {
      toast.error('Apple Wallet logo is required', {
        description: 'Please upload an Apple logo (480×150 rectangular or 150×150 square)'
      });
      return false;
    }

    if (!formData.googleLogoUrl && !formData.googleLogoFile) {
      toast.error('Google Wallet logo is required', {
        description: 'Please upload a Google logo (660×660 square)'
      });
      return false;
    }

    if (!formData.heroImageUrl && !formData.heroImageFile) {
      toast.error('Hero image is required', {
        description: 'Please upload a hero image (1125×432)'
      });
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsCreating(true);

      // Upload images if provided
      let appleLogoUrls = offer?.apple_logo_urls || null;
      let googleLogoUrl = offer?.google_logo_url || formData.googleLogoUrl;
      let heroImageUrls = offer?.hero_image_urls || null;

      // Upload Apple logo (rectangular or square, multi-resolution)
      if (formData.appleLogoFile) {
        appleLogoUrls = await uploadMultipleResolutions(
          formData.appleLogoFile,
          'appleLogo',
          formData.appleLogoType
        );
      }

      // Upload Google logo (square only, single resolution)
      if (formData.googleLogoFile) {
        googleLogoUrl = await uploadSingleImage(formData.googleLogoFile, 'googleLogo', '3x');
      }

      // Upload hero/strip images (multi-resolution for Apple)
      if (formData.heroImageFile) {
        heroImageUrls = await uploadMultipleResolutions(
          formData.heroImageFile,
          'heroImage'
        );
      }

      const offerData = {
        business_id: businessId,
        header: formData.header.trim(),
        name: formData.name.trim(),
        description: null,
        discount_type: formData.discountType,
        discount_value: formData.discountType === 'free_item' ? null : formData.discountValue,
        minimum_purchase: 0,
        valid_from: new Date(formData.validFrom).toISOString(),
        valid_until: formData.validUntil ? new Date(formData.validUntil).toISOString() : null,
        usage_limit_per_customer: 1,
        total_usage_limit: null,
        background_color: formData.backgroundColor,
        apple_logo_urls: appleLogoUrls,
        google_logo_url: googleLogoUrl,
        hero_image_urls: heroImageUrls,
        updated_at: new Date().toISOString()
      };

      if (offer) {
        // Update existing offer
        const { data, error } = await supabase
          .from('coupon_offers')
          .update(offerData)
          .eq('id', offer.id)
          .select()
          .single();

        if (error) throw error;

        // Delete old images that were replaced
        await deleteReplacedCouponImages(offer, {
          appleLogoUrls: formData.appleLogoFile ? appleLogoUrls : undefined,
          googleLogoUrl: formData.googleLogoFile ? googleLogoUrl : undefined,
          heroImageUrls: formData.heroImageFile ? heroImageUrls : undefined
        });

        // Update Google Wallet class
        await createGoogleWalletClass(data);

        clearDraft();
        toast.success('Coupon offer updated successfully!');
        onComplete(data);
      } else {
        // Create new offer
        const { data, error } = await supabase
          .from('coupon_offers')
          .insert([offerData])
          .select()
          .single();

        if (error) throw error;

        setSavedCouponId(data.id);

        // Create Google Wallet class for the new coupon
        await createGoogleWalletClass(data);

        clearDraft();
        toast.success('Coupon offer created successfully!');
        onComplete(data);
      }
    } catch (error) {
      console.error('Error saving coupon offer:', error);

      if (error instanceof Error) {
        if (error.message.includes('upload failed')) {
          toast.error('Image upload failed', {
            description: 'Please check your image files and try again.'
          });
        } else if (error.message.includes('storage')) {
          toast.error('Storage error', {
            description: 'Please contact your administrator.'
          });
        } else {
          toast.error(offer ? 'Failed to update coupon offer' : 'Failed to create coupon offer', {
            description: error.message
          });
        }
      } else {
        toast.error(offer ? 'Failed to update coupon offer' : 'Failed to create coupon offer', {
          description: 'Please try again.'
        });
      }
    } finally {
      setIsCreating(false);
    }
  };

  const createGoogleWalletClass = async (couponData: any) => {
    try {
      console.log('Creating Google Wallet class for coupon:', couponData.id);

      const passGeneratorUrl = import.meta.env.VITE_PASS_GENERATOR_URL || 'https://pass.taginn.app';

      const response = await fetch(`${passGeneratorUrl}/google-wallet/create-offer-class`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          class_suffix: `coupon_${couponData.id}`,
          class_data: {
            issuerName: couponData.header || 'TagInn Business',
            title: couponData.name,
            provider: couponData.header || 'TagInn Business',
            redemptionChannel: 'BOTH',
            logoUrl: couponData.google_logo_url || couponData.logo_url,
            heroImageUrl: couponData.hero_image_urls?.['3x'] || couponData.hero_image_url,
            backgroundColor: couponData.background_color || PRIMARY_COLOR,
            description: `${couponData.discount_type === 'percentage' ? couponData.discount_value + '% off' : couponData.discount_type === 'fixed_amount' ? '$' + couponData.discount_value + ' off' : 'Free item'}`
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Google Wallet class created:', result.id);

        const { error: updateError } = await supabase
          .from('coupon_offers')
          .update({ google_class_id: result.id })
          .eq('id', couponData.id);

        if (updateError) {
          console.error('Error updating coupon with Google class ID:', updateError);
        }
      } else {
        console.warn('Failed to create Google Wallet class:', response.statusText);
      }
    } catch (error) {
      console.warn('Google Wallet class creation failed:', error);
    }
  };

  return (
    <div className="space-y-6 relative">
      {/* Global drag overlay - blurs everything except upload zones */}
      {isDragging && (
        <div className="fixed inset-0 z-[45] pointer-events-none">
          <div className="absolute inset-0 backdrop-blur-md bg-background/50" />
        </div>
      )}

      <div className="lg:w-1/2 lg:pr-4">
        {/* Left Panel - Form */}
        <Card className="h-fit">
          <CardHeader className="relative pb-3 flex-shrink-0">
            <div>
              <CardTitle className="text-sm font-medium">
                {offer ? 'Edit Coupon Offer' : 'Coupon Offer Builder'}
              </CardTitle>
              <CardDescription>
                {offer
                  ? 'Update your coupon offer design and settings.'
                  : 'Customize your coupon offer for customers to save to their mobile wallets.'}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => { clearDraft(); onCancel(); }} className="absolute top-4 right-4 h-8 w-8 p-0">
              <span className="sr-only">Close</span>
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Offer Name */}
            <div>
              <Label htmlFor="name">Offer name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="20% Off Your Next Purchase"
                className="mt-1"
              />
            </div>

            {/* Header */}
            <div>
              <Label htmlFor="header">Header</Label>
              <Input
                id="header"
                value={formData.header}
                onChange={(e) => setFormData(prev => ({ ...prev, header: e.target.value }))}
                placeholder="Your Business Name"
                className="mt-1"
              />
            </div>

            {/* Discount Type */}
            <div>
              <Label htmlFor="discountType">Discount Type</Label>
              <Select
                value={formData.discountType}
                onValueChange={(value) => setFormData(prev => ({ ...prev, discountType: value as 'percentage' | 'fixed_amount' | 'free_item' }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select discount type" />
                </SelectTrigger>
                <SelectContent>
                  {(['percentage', 'fixed_amount', 'free_item'] as const).map((type) => {
                    const config = getDiscountTypeConfig(type);
                    return (
                      <SelectItem key={type} value={type}>
                        <div className="flex items-center gap-2">
                          {config.icon}
                          <span>{config.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500 mt-2">
                {getDiscountTypeConfig(formData.discountType).description}
              </p>
            </div>

            {/* Discount Value */}
            {formData.discountType !== 'free_item' && (
              <div>
                <Label htmlFor="discountValue">
                  Discount value {formData.discountType === 'percentage' ? '(%)' : '($)'}
                </Label>
                <Input
                  id="discountValue"
                  type="number"
                  step={formData.discountType === 'percentage' ? '1' : '0.01'}
                  min="0"
                  max={formData.discountType === 'percentage' ? 100 : undefined}
                  value={formData.discountValue}
                  onChange={(e) => setFormData(prev => ({ ...prev, discountValue: parseFloat(e.target.value) || 0 }))}
                  placeholder={formData.discountType === 'percentage' ? '10' : '5.00'}
                  className="mt-1"
                />
              </div>
            )}

            {/* Validity Period */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="validFrom">Valid from</Label>
                <Input
                  id="validFrom"
                  type="date"
                  value={formData.validFrom}
                  onChange={(e) => setFormData(prev => ({ ...prev, validFrom: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="validUntil">Valid until (optional)</Label>
                <Input
                  id="validUntil"
                  type="date"
                  value={formData.validUntil}
                  onChange={(e) => setFormData(prev => ({ ...prev, validUntil: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Logos side by side */}
            <div className="grid grid-cols-2 gap-4">
              {/* Apple Wallet Logo */}
              <div className={`${isDragging ? 'relative z-50' : ''}`}>
                <Label htmlFor="apple-logo-upload" className={`${isDragging ? 'relative z-50' : ''}`}>
                  Apple Wallet Logo <span className="text-red-500">*</span>
                </Label>
                <div
                  className={`mt-1 border-2 border-dashed rounded-lg p-3 hover:border-primary/50 transition-all cursor-pointer ${
                    dragTarget === 'appleLogo' ? 'border-primary bg-primary/10 scale-105 shadow-lg' : ''
                  } ${isDragging ? 'relative z-50 bg-background' : ''}`}
                  onClick={() => document.getElementById('apple-logo-upload')?.click()}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    if (dragTargetTimeoutRef.current) {
                      clearTimeout(dragTargetTimeoutRef.current);
                      dragTargetTimeoutRef.current = null;
                    }
                    setDragTarget('appleLogo');
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragTargetTimeoutRef.current) {
                      clearTimeout(dragTargetTimeoutRef.current);
                      dragTargetTimeoutRef.current = null;
                    }
                    setDragTarget('appleLogo');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    if (dragTargetTimeoutRef.current) {
                      clearTimeout(dragTargetTimeoutRef.current);
                    }
                    dragTargetTimeoutRef.current = setTimeout(() => {
                      setDragTarget(null);
                      dragTargetTimeoutRef.current = null;
                    }, 10);
                  }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      await processImageFile(file, 'appleLogo');
                    }
                  }}
                >
                  {(formData.appleLogoBase64 || formData.appleLogoUrl) ? (
                    <div className="flex items-center gap-2">
                      <img
                        src={formData.appleLogoBase64 || formData.appleLogoUrl}
                        alt="Apple logo preview"
                        className="h-12 object-contain rounded"
                      />
                      <div className="flex-1 min-w-0" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage('appleLogo');
                        }}
                        className="h-6 w-6 p-0"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-center py-1">
                      <Upload className="h-6 w-6 text-muted-foreground" />
                      <div className="text-xs text-muted-foreground">
                        <p className="font-medium">Click or drop</p>
                        <p className="text-xs mt-0.5 font-medium">480×150 or 150×150</p>
                        <p className="text-[10px] mt-0.5">PNG required</p>
                      </div>
                    </div>
                  )}
                </div>
                <input
                  id="apple-logo-upload"
                  type="file"
                  accept="image/png"
                  onChange={(e) => handleImageUpload(e, 'appleLogo')}
                  className="hidden"
                />
              </div>

              {/* Google Wallet Logo */}
              <div className={`${isDragging ? 'relative z-50' : ''}`}>
                <Label htmlFor="google-logo-upload" className={`${isDragging ? 'relative z-50' : ''}`}>
                  Google Wallet Logo <span className="text-red-500">*</span>
                </Label>
                <div
                  className={`mt-1 border-2 border-dashed rounded-lg p-3 hover:border-primary/50 transition-all cursor-pointer ${
                    dragTarget === 'googleLogo' ? 'border-primary bg-primary/10 scale-105 shadow-lg' : ''
                  } ${isDragging ? 'relative z-50 bg-background' : ''}`}
                  onClick={() => document.getElementById('google-logo-upload')?.click()}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    if (dragTargetTimeoutRef.current) {
                      clearTimeout(dragTargetTimeoutRef.current);
                      dragTargetTimeoutRef.current = null;
                    }
                    setDragTarget('googleLogo');
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragTargetTimeoutRef.current) {
                      clearTimeout(dragTargetTimeoutRef.current);
                      dragTargetTimeoutRef.current = null;
                    }
                    setDragTarget('googleLogo');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    if (dragTargetTimeoutRef.current) {
                      clearTimeout(dragTargetTimeoutRef.current);
                    }
                    dragTargetTimeoutRef.current = setTimeout(() => {
                      setDragTarget(null);
                      dragTargetTimeoutRef.current = null;
                    }, 10);
                  }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      await processImageFile(file, 'googleLogo');
                    }
                  }}
                >
                  {(formData.googleLogoBase64 || formData.googleLogoUrl) ? (
                    <div className="flex items-center gap-2">
                      <img
                        src={formData.googleLogoBase64 || formData.googleLogoUrl}
                        alt="Google logo preview"
                        className="h-12 w-12 object-contain rounded"
                      />
                      <div className="flex-1" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage('googleLogo');
                        }}
                        className="h-6 w-6 p-0"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-center py-1">
                      <Upload className="h-6 w-6 text-muted-foreground" />
                      <div className="text-xs text-muted-foreground">
                        <p className="font-medium">Click or drop</p>
                        <p className="text-xs mt-0.5 font-medium">660×660</p>
                        <p className="text-[10px] mt-0.5">PNG required</p>
                      </div>
                    </div>
                  )}
                </div>
                <input
                  id="google-logo-upload"
                  type="file"
                  accept="image/png"
                  onChange={(e) => handleImageUpload(e, 'googleLogo')}
                  className="hidden"
                />
              </div>
            </div>

            {/* Hero Image - Full width below logos */}
            <div className={`${isDragging ? 'relative z-50' : ''}`}>
              <Label htmlFor="hero-image-upload" className={`${isDragging ? 'relative z-50' : ''}`}>
                Hero image <span className="text-red-500">*</span>
              </Label>
              <div
                className={`mt-1 border-2 border-dashed rounded-lg p-3 hover:border-primary/50 transition-all cursor-pointer ${
                  dragTarget === 'heroImage' ? 'border-primary bg-primary/10 scale-[1.02] shadow-lg' : ''
                } ${isDragging ? 'relative z-50 bg-background' : ''}`}
                onClick={() => document.getElementById('hero-image-upload')?.click()}
                onDragEnter={(e) => {
                  e.preventDefault();
                  if (dragTargetTimeoutRef.current) {
                    clearTimeout(dragTargetTimeoutRef.current);
                    dragTargetTimeoutRef.current = null;
                  }
                  setDragTarget('heroImage');
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragTargetTimeoutRef.current) {
                    clearTimeout(dragTargetTimeoutRef.current);
                    dragTargetTimeoutRef.current = null;
                  }
                  setDragTarget('heroImage');
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  if (dragTargetTimeoutRef.current) {
                    clearTimeout(dragTargetTimeoutRef.current);
                  }
                  dragTargetTimeoutRef.current = setTimeout(() => {
                    setDragTarget(null);
                    dragTargetTimeoutRef.current = null;
                  }, 10);
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    await processImageFile(file, 'heroImage');
                  }
                }}
              >
                {(formData.heroImageBase64 || formData.heroImageUrl) ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={formData.heroImageBase64 || formData.heroImageUrl}
                      alt="Hero image preview"
                      className="h-16 w-auto object-contain rounded"
                    />
                    <div className="flex-1" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage('heroImage');
                      }}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1 text-center py-1.5">
                    <Upload className="h-6 w-6 text-muted-foreground" />
                    <div className="text-xs text-muted-foreground">
                      <p className="font-medium">Click or drop</p>
                      <p className="text-xs mt-0.5 font-medium">1125×432</p>
                      <p className="text-[10px] mt-0.5">PNG required</p>
                    </div>
                  </div>
                )}
              </div>
              <input
                id="hero-image-upload"
                type="file"
                accept="image/png"
                onChange={(e) => handleImageUpload(e, 'heroImage')}
                className="hidden"
              />
            </div>

            {/* Background Color */}
            <div>
              <Label htmlFor="backgroundColor">Background color</Label>
              <div className="flex items-center gap-2 mt-1">
                {/* Color Picker Preview */}
                <input
                  id="backgroundColorPicker"
                  type="color"
                  value={formData.backgroundColor}
                  onChange={(e) => {
                    const newColor = e.target.value;

                    // Clear any pending timeout
                    if (colorUpdateTimeoutRef.current) {
                      clearTimeout(colorUpdateTimeoutRef.current);
                    }

                    // Debounce the update - only update after user stops dragging for 150ms
                    colorUpdateTimeoutRef.current = setTimeout(() => {
                      setFormData(prev => ({ ...prev, backgroundColor: newColor }));
                    }, 150);
                  }}
                  className="w-10 h-10 rounded border cursor-pointer"
                  aria-label="Background color picker"
                />

                {/* Hex Input */}
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">Hex:</span>
                  <Input
                    id="backgroundColor"
                    value={formData.backgroundColor}
                    onChange={(e) => {
                      const hex = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(hex)) {
                        setFormData(prev => ({ ...prev, backgroundColor: hex }));
                      }
                    }}
                    className="font-mono w-24 h-9"
                    placeholder="#d97c12"
                    maxLength={7}
                  />
                </div>

                {/* RGB Inputs */}
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">R:</span>
                  <Input
                    type="number"
                    min="0"
                    max="255"
                    value={parseInt(formData.backgroundColor.slice(1, 3), 16) || 0}
                    onChange={(e) => {
                      const r = Math.min(255, Math.max(0, parseInt(e.target.value) || 0));
                      const g = parseInt(formData.backgroundColor.slice(3, 5), 16) || 0;
                      const b = parseInt(formData.backgroundColor.slice(5, 7), 16) || 0;
                      const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                      setFormData(prev => ({ ...prev, backgroundColor: hex }));
                    }}
                    className="w-16 h-9"
                  />
                </div>

                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">G:</span>
                  <Input
                    type="number"
                    min="0"
                    max="255"
                    value={parseInt(formData.backgroundColor.slice(3, 5), 16) || 0}
                    onChange={(e) => {
                      const r = parseInt(formData.backgroundColor.slice(1, 3), 16) || 0;
                      const g = Math.min(255, Math.max(0, parseInt(e.target.value) || 0));
                      const b = parseInt(formData.backgroundColor.slice(5, 7), 16) || 0;
                      const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                      setFormData(prev => ({ ...prev, backgroundColor: hex }));
                    }}
                    className="w-16 h-9"
                  />
                </div>

                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">B:</span>
                  <Input
                    type="number"
                    min="0"
                    max="255"
                    value={parseInt(formData.backgroundColor.slice(5, 7), 16) || 0}
                    onChange={(e) => {
                      const r = parseInt(formData.backgroundColor.slice(1, 3), 16) || 0;
                      const g = parseInt(formData.backgroundColor.slice(3, 5), 16) || 0;
                      const b = Math.min(255, Math.max(0, parseInt(e.target.value) || 0));
                      const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                      setFormData(prev => ({ ...prev, backgroundColor: hex }));
                    }}
                    className="w-16 h-9"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { clearDraft(); onCancel(); }}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isCreating}
                className="flex items-center gap-2"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {offer ? 'Updating Offer...' : 'Creating Offer...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {offer ? 'Update Coupon Offer' : 'Create Coupon Offer'}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right Panel - Live Preview */}
      <div className="lg:fixed lg:top-32 lg:right-0 lg:w-1/2 space-y-3 mt-8 lg:mt-0 lg:pl-4">
        <UnifiedPassPreview
          key={`${formData.name}-${formData.appleLogoBase64}-${formData.googleLogoBase64}-${formData.heroImageBase64}`}
          data={{
            type: 'coupon_simple',
            businessName: formData.header || businessName || 'Your Business',
            programName: formData.name || 'Coupon Offer',
            programType: 'coupon',
            backgroundColor: formData.backgroundColor,
            heroImageUrl: formData.heroImageBase64 || formData.heroImageUrl,
          }}
          passData={{
            google_logo_url: formData.googleLogoBase64 || formData.googleLogoUrl,
            apple_logo_urls: formData.appleLogoBase64 || formData.appleLogoUrl ? {
              '1x': formData.appleLogoBase64 || formData.appleLogoUrl,
              '2x': formData.appleLogoBase64 || formData.appleLogoUrl,
              '3x': formData.appleLogoBase64 || formData.appleLogoUrl
            } : undefined,
            hero_image_urls: formData.heroImageBase64 || formData.heroImageUrl ? {
              '1x': formData.heroImageBase64 || formData.heroImageUrl,
              '2x': formData.heroImageBase64 || formData.heroImageUrl,
              '3x': formData.heroImageBase64 || formData.heroImageUrl
            } : undefined
          }}
          mode="creation"
          showPlatformToggle={false}
          heroImagePreview={formData.heroImageBase64}
          customerName="John Doe"
          onBusinessNameClick={() => {
            const headerInput = document.getElementById('header');
            if (headerInput) {
              headerInput.focus();
              headerInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }}
          onAppleLogoClick={() => {
            const uploadArea = document.getElementById('apple-logo-upload')?.parentElement;
            if (uploadArea) {
              uploadArea.scrollIntoView({ behavior: 'smooth', block: 'center' });
              uploadArea.classList.add('ring-2', 'ring-primary');
              setTimeout(() => {
                uploadArea.classList.remove('ring-2', 'ring-primary');
              }, 2000);
            }
          }}
          onGoogleLogoClick={() => {
            const uploadArea = document.getElementById('google-logo-upload')?.parentElement;
            if (uploadArea) {
              uploadArea.scrollIntoView({ behavior: 'smooth', block: 'center' });
              uploadArea.classList.add('ring-2', 'ring-primary');
              setTimeout(() => {
                uploadArea.classList.remove('ring-2', 'ring-primary');
              }, 2000);
            }
          }}
          onProgramNameClick={() => {
            const nameInput = document.getElementById('name');
            if (nameInput) {
              nameInput.focus();
              nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }}
          onHeroImageClick={() => {
            const uploadArea = document.getElementById('hero-image-upload')?.parentElement;
            if (uploadArea) {
              uploadArea.scrollIntoView({ behavior: 'smooth', block: 'center' });
              uploadArea.classList.add('ring-2', 'ring-primary');
              setTimeout(() => {
                uploadArea.classList.remove('ring-2', 'ring-primary');
              }, 2000);
            }
          }}
        />
      </div>
    </div>
  );
};

export default CouponOfferBuilder;
